import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BLOGS } from '../data/blogData';
import Navbar from './Navbar';
import Footer from './Footer';
import Seo from './Seo';
import { FaCalendar, FaUser, FaArrowRight } from 'react-icons/fa';

const SITE_URL = 'https://eazypaisa.com';

const blogsSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'EazyPaisa',
      url: `${SITE_URL}/`,
    },
    {
      '@type': 'Blog',
      '@id': `${SITE_URL}/blogs#blog`,
      url: `${SITE_URL}/blogs`,
      name: 'EazyPaisa Blog',
      description: 'Tips, guides, and insights on loans and managing your money.',
      inLanguage: 'en-IN',
      publisher: { '@id': `${SITE_URL}/#organization` },
      blogPost: BLOGS.map((blog) => {
        const postUrl = `${SITE_URL}/blogs/${blog.slug}`;
        return {
          '@type': 'BlogPosting',
          '@id': `${postUrl}#blogposting`,
          url: postUrl,
          headline: blog.title,
          description: blog.excerpt,
          image: new URL(blog.image, SITE_URL).href,
          datePublished: blog.date,
          author: {
            '@type': 'Organization',
            name: blog.author,
            url: `${SITE_URL}/`,
          },
          publisher: { '@id': `${SITE_URL}/#organization` },
          mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
          inLanguage: 'en-IN',
        };
      }),
    },
  ],
};

export function Blogs() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen w-full bg-[#F7F4ED] flex flex-col">
      <Seo
        title="Blog"
        description="Tips, guides, and insights on personal loans, EMI planning, and managing your finances from EazyPaisa."
        path="/blogs"
        schema={blogsSchema}
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="w-full pt-36 pb-16 sm:pt-40 md:pt-44 px-4">
          <div className="page-container text-center">
            <h1 className="heading-font text-4xl sm:text-5xl font-bold text-[#1F3E33] mb-4 drop-shadow-lg">
              EazyPaisa Blog
            </h1>
            <p className="text-lg sm:text-xl text-[#657069] font-['Inter']">
              Tips, guides, and insights on loans and managing your money.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full px-4 py-16" ref={sectionRef}>
          <div className={`page-container fade-in-section ${isVisible ? "is-visible" : ""}`}>
            {BLOGS.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-[#657069] text-xl font-['Inter']">No blog posts available yet.</p>
                <p className="text-gray-400 text-sm mt-2 font-['Inter']">Check back soon for updates!</p>
              </div>
            ) : (
              <div className="flex flex-col gap-16 w-full">
                {/* Featured posts: alternating image/text rows */}
                {BLOGS.slice(0, 2).map((blog, index) => {
                  const imageOrder = index % 2 === 0 ? "order-1" : "order-1 md:order-2";
                  const textOrder = index % 2 === 0 ? "order-2" : "order-2 md:order-1";
                  const imageBlock = (
                    <div className={`overflow-hidden rounded-2xl border border-[#D3D8D0] shadow-lg aspect-[4/3] ${imageOrder}`}>
                      <img
                        src={blog.image}
                        alt={blog.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  );
                  const textBlock = (
                    <div className={`flex flex-col gap-5 ${textOrder}`}>
                      <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm font-['Inter']">
                        <span className="flex items-center gap-2">
                          <FaUser className="text-xs" aria-hidden="true" />
                          {blog.author}
                        </span>
                        <span className="flex items-center gap-2">
                          <FaCalendar className="text-xs" aria-hidden="true" />
                          {formatDate(blog.date)}
                        </span>
                      </div>
                      <h2 className="heading-font text-3xl md:text-4xl font-bold text-[#1F3E33] leading-tight">
                        {blog.title}
                      </h2>
                      <p className="text-[#657069] text-lg font-['Inter'] leading-relaxed">
                        {blog.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 self-start px-6 py-3 rounded-full bg-[#1F3E33] text-white font-semibold font-['Inter'] transition-colors group-hover:bg-[#152C24]">
                        Read More
                        <FaArrowRight className="text-sm" aria-hidden="true" />
                      </span>
                    </div>
                  );
                  return (
                    <Link
                      key={blog.slug}
                      to={`/blogs/${blog.slug}`}
                      className={`group grid md:grid-cols-2 gap-6 md:gap-10 lg:gap-14 items-center slide-in-left ${isVisible ? "is-visible" : ""}`}
                      style={{ transitionDelay: `${index * 0.1}s` }}
                    >
                      {imageBlock}
                      {textBlock}
                    </Link>
                  );
                })}

                {/* Remaining posts: side by side cards */}
                {BLOGS.length > 2 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {BLOGS.slice(2).map((blog, index) => (
                      <Link
                        key={blog.slug}
                        to={`/blogs/${blog.slug}`}
                        className={`group block w-full slide-in-left ${isVisible ? "is-visible" : ""}`}
                        style={{ transitionDelay: `${(index + 2) * 0.1}s` }}
                      >
                        <article className="overflow-hidden h-full flex flex-col md:bg-white md:rounded-2xl md:border-2 md:border-[#D3D8D0] md:shadow-md md:hover:shadow-xl md:hover:border-[#1F3E33] transition-all duration-300 md:hover:-translate-y-2">
                          <div className="w-full aspect-video overflow-hidden">
                            <img
                              src={blog.image}
                              alt={blog.title}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <div className="p-6 flex flex-col flex-grow">
                            <h2 className="heading-font text-xl font-bold text-[#1F3E33] mb-3 group-hover:text-[#1F3E33] transition-colors line-clamp-2">
                              {blog.title}
                            </h2>

                            <p className="text-[#657069] font-['Inter'] mb-4 line-clamp-3 flex-grow">
                              {blog.excerpt}
                            </p>

                            <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm mt-auto font-['Inter']">
                              <span className="flex items-center gap-2">
                                <FaUser className="text-xs" aria-hidden="true" />
                                {blog.author}
                              </span>
                              <span className="flex items-center gap-2">
                                <FaCalendar className="text-xs" aria-hidden="true" />
                                {formatDate(blog.date)}
                              </span>
                            </div>

                            <span className="flex items-center gap-2 text-[#1F3E33] font-semibold mt-4 group-hover:gap-3 transition-all font-['Inter']">
                              Read More
                              <FaArrowRight className="text-sm" aria-hidden="true" />
                            </span>
                          </div>
                        </article>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Blogs;
